import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import { Workflow, Plus, Zap, GitBranch, Play, ArrowRight } from "lucide-react";

export default function Automation() {
  const qc = useQueryClient();
  const { data: automations = [] } = useQuery({ queryKey: ["automations"], queryFn: () => api.get("/automations").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", trigger: "New Lead", condition: "Always", actions: "" });

  const toggle = async (id) => {
    await api.put(`/automations/${id}/toggle`);
    qc.invalidateQueries({ queryKey: ["automations"] });
  };

  const create = async () => {
    if (!form.name) { toast.error("Nama diperlukan"); return; }
    await api.post("/automations", {
      name: form.name, trigger: form.trigger, condition: form.condition,
      actions: form.actions ? form.actions.split(",").map((a) => a.trim()) : [], active: true,
    });
    toast.success("Automasi dicipta");
    setOpen(false);
    setForm({ name: "", trigger: "New Lead", condition: "Always", actions: "" });
    qc.invalidateQueries({ queryKey: ["automations"] });
  };

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto" data-testid="automation-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Workflow className="h-6 w-6 text-primary" /> Automation Builder</h1>
          <p className="text-muted-foreground text-sm mt-1">WHEN pelanggan buat X · IF syarat Y · THEN lakukan Z.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="automation-add-node-button" className="rounded-full"><Plus className="h-4 w-4 mr-1.5" /> Automasi Baru</Button>
          </DialogTrigger>
          <DialogContent data-testid="automation-dialog">
            <DialogHeader><DialogTitle>Cipta Automasi</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Nama</Label><Input data-testid="automation-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">WHEN (Trigger)</Label><Input data-testid="automation-trigger-input" value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">IF (Condition)</Label><Input data-testid="automation-condition-input" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">THEN (Actions, dipisah koma)</Label><Input data-testid="automation-actions-input" value={form.actions} onChange={(e) => setForm({ ...form, actions: e.target.value })} placeholder="AI Welcome, Qualify, Assign Staff" className="mt-1" /></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="automation-save-button" className="rounded-full w-full">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {automations.map((a) => (
          <div key={a.id} data-testid={`automation-card-${a.id}`} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${a.active ? "bg-accent text-primary" : "bg-secondary text-muted-foreground"}`}>
                  <Zap className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-display font-semibold">{a.name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Play className="h-3 w-3" /> {a.runs} kali dijalankan</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`${a.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"} border-0 rounded-full`}>{a.active ? "Aktif" : "Off"}</Badge>
                <Switch checked={a.active} onCheckedChange={() => toggle(a.id)} data-testid={`automation-toggle-${a.id}`} />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap text-xs">
              <span className="px-2.5 py-1.5 rounded-lg bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 font-medium flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" /> WHEN: {a.trigger}</span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="px-2.5 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-medium flex items-center gap-1.5"><GitBranch className="h-3.5 w-3.5" /> IF: {a.condition}</span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
              {a.actions.map((act) => (
                <span key={act} className="px-2.5 py-1.5 rounded-lg bg-accent text-accent-foreground font-medium">THEN: {act}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
